import { useQuery } from '@tanstack/react-query'
import { api } from '../api'
import { LastUpdated } from './LastUpdated'

/**
 * Header del corp en el dashboard. Muestra logo, nombre, ticker, alliance
 * (si tiene), y un par de stats: miembros, tax rate. Sin nav, sin acciones —
 * es puramente decorativo/informativo.
 */
export function CorpHeader() {
  const { data, isLoading, dataUpdatedAt, error } = useQuery({
    queryKey: ['corp-profile'],
    queryFn: api.corpProfile,
    staleTime: 60 * 60_000,  // corp profile cambia poco, 1h está bien
  })

  if (isLoading) {
    return (
      <div className="card flex items-center gap-4">
        <div className="w-16 h-16 rounded-md bg-eve-bg border border-eve-border animate-pulse" />
        <div className="flex-1 space-y-2">
          <div className="h-5 w-5/12 bg-eve-bg border border-eve-border rounded animate-pulse" />
          <div className="h-3 w-3/12 bg-eve-bg border border-eve-border rounded animate-pulse" />
        </div>
      </div>
    )
  }

  if (error || !data?.profile) {
    return null  // No bloqueamos el dashboard si el corp fetch falla.
  }

  const p = data.profile
  const logoUrl = `https://images.evetech.net/corporations/${p.corporation_id}/logo?size=128`

  return (
    <div className="card">
      <div className="flex items-center gap-4">
        <img
          src={logoUrl}
          alt={p.name}
          className="w-16 h-16 rounded-md border border-eve-border bg-eve-bg"
          loading="lazy"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h2 className="text-xl font-bold truncate">{p.name}</h2>
            <code className="text-xs text-eve-muted font-mono">[{p.ticker}]</code>
          </div>
          {p.alliance_name && (
            <div className="text-sm text-eve-muted">
              {p.alliance_name}{' '}
              {p.alliance_ticker && (
                <code className="text-xs font-mono">[{p.alliance_ticker}]</code>
              )}
            </div>
          )}
          <div className="text-xs text-eve-muted mt-1">
            <LastUpdated timestamp={dataUpdatedAt} label="Corp" />
          </div>
        </div>
        <div className="hidden sm:flex flex-col items-end gap-1 shrink-0">
          <Stat label="Miembros" value={p.member_count.toLocaleString('en-US')} />
          <Stat label="Tax" value={`${(p.tax_rate * 100).toFixed(1)}%`} />
        </div>
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-right">
      <div className="stat-label">{label}</div>
      <div className="font-mono text-sm">{value}</div>
    </div>
  )
}